import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AgentService } from '../../../../services/forwarding/agent.service';
import { Agent, CreateAgentDto, UpdateAgentDto } from '../../../../models/forwarding/agent.model';

@Component({
  selector: 'app-agent-management-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agent-management-modal.html',
  styleUrl: './agent-management-modal.css'
})
export class AgentManagementModalComponent implements OnInit, OnDestroy {
  showModal = false;
  showAddModal = false;
  showEditModal = false;
  agents: Agent[] = [];
  filteredAgents: Agent[] = [];
  searchTerm = '';
  private subscriptions = new Subscription();

  formData: CreateAgentDto = {
    name: '',
    contactInformation: '',
    isActive: true
  };

  editFormData: UpdateAgentDto = {
    name: '',
    contactInformation: '',
    isActive: true
  };

  currentEditId: number | null = null;

  constructor(private agentService: AgentService) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.agentService.getAgents().subscribe(agents => {
        this.agents = agents;
        this.filteredAgents = agents;
        console.log('[AgentManagementModal] Agents updated:', agents.length);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  open(): void {
    this.showModal = true;
    console.log('[AgentManagementModal] Modal opened');
  }

  close(): void {
    this.showModal = false;
    console.log('[AgentManagementModal] Modal closed');
  }

  openAddAgentModal(): void {
    this.showAddModal = true;
    this.resetForm();
    console.log('[AgentManagementModal] Add modal opened');
  }

  closeAddAgentModal(): void {
    this.showAddModal = false;
    this.resetForm();
  }

  openEditAgentModal(agent: Agent): void {
    this.showEditModal = true;
    this.currentEditId = agent.id;
    this.editFormData = {
      name: agent.name,
      contactInformation: agent.contactInformation || '',
      isActive: agent.isActive
    };
    console.log('[AgentManagementModal] Edit modal opened for agent:', agent.id);
  }

  closeEditAgentModal(): void {
    this.showEditModal = false;
    this.currentEditId = null;
    this.editFormData = { name: '', contactInformation: '', isActive: true };
  }

  filterAgents(): void {
    const search = this.searchTerm.toLowerCase().trim();
    if (!search) {
      this.filteredAgents = this.agents;
      return;
    }

    this.filteredAgents = this.agents.filter(a =>
      a.name.toLowerCase().includes(search) ||
      (a.contactInformation && a.contactInformation.toLowerCase().includes(search))
    );
  }

  createAgent(): void {
    if (!this.formData.name) {
      alert('Please enter an agent name');
      return;
    }

    this.agentService.create(this.formData).subscribe({
      next: (agent) => {
        console.log('[AgentManagementModal] Agent created successfully:', agent);
        this.closeAddAgentModal();
      },
      error: (error) => {
        console.error('[AgentManagementModal] Error creating agent:', error);
        alert('Error creating agent. Please try again.');
      }
    });
  }

  updateAgent(): void {
    if (!this.currentEditId || !this.editFormData.name) {
      alert('Please enter an agent name');
      return;
    }

    this.agentService.update(this.currentEditId, this.editFormData).subscribe({
      next: (agent) => {
        console.log('[AgentManagementModal] Agent updated successfully:', agent);
        this.closeEditAgentModal();
      },
      error: (error) => {
        console.error('[AgentManagementModal] Error updating agent:', error);
        alert('Error updating agent. Please try again.');
      }
    });
  }

  deleteAgent(id: number): void {
    if (!confirm('Are you sure you want to delete this agent?')) {
      return;
    }

    this.agentService.delete(id).subscribe({
      next: () => {
        console.log('[AgentManagementModal] Agent deleted successfully');
      },
      error: (error) => {
        console.error('[AgentManagementModal] Error deleting agent:', error);
        alert('Error deleting agent. Please try again.');
      }
    });
  }

  truncateText(text: string | undefined, maxLength: number = 50): string {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  private resetForm(): void {
    this.formData = {
      name: '',
      contactInformation: '',
      isActive: true
    };
  }
}
